/**
 * Индикаторы, которым мало одного ряда цен.
 *
 * В `math.ts` лежит то, что считается по одному ряду чисел: скользящие,
 * RSI, отклонение. Здесь — то, чему нужны максимум, минимум, закрытие и
 * объём вместе: осцилляторы диапазона, каналы, объёмные индикаторы.
 * Разделение не формальное — функции из `math.ts` применимы к любому
 * ряду, включая ряд значений другого индикатора, а эти работают только
 * со свечами.
 *
 * Как и там, `null` означает «данных ещё не хватило». Заполнить
 * прогревочный участок нулями или первым значением значит нарисовать на
 * графике линию, которой не было, и дать правилу сработать там, где
 * считать было не по чему.
 */
import { atr, ema, sma, trueRange } from './math.js';
function filled(length) {
    return new Array(length).fill(null);
}
/**
 * Взвешенное скользящее среднее.
 *
 * Вес растёт линейно к последнему бару: старое учитывается, но меньше.
 * Отличие от EMA в том, что окно конечно — за его пределами прошлое не
 * влияет вовсе, и иногда нужно именно это.
 */
export function wma(values, period) {
    const out = filled(values.length);
    if (period <= 0 || values.length < period)
        return out;
    const denominator = (period * (period + 1)) / 2;
    /*
     * Скользящее окно вместо пересчёта.
     *
     * Прямой способ — сложить `period` произведений на каждом баре — при
     * длинном периоде на пяти тысячах баров превращается в миллионы
     * операций. Здесь на каждом шаге поддерживаются две суммы: взвешенная
     * и простая. При сдвиге окна взвешенная уменьшается на простую и
     * прирастает новым значением, умноженным на полный вес.
     */
    let weighted = 0;
    let plain = 0;
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (i < period) {
            weighted += value * (i + 1);
            plain += value;
            if (i === period - 1)
                out[i] = weighted / denominator;
            continue;
        }
        const leaving = values[i - period];
        weighted = weighted - plain + value * period;
        plain = plain - leaving + value;
        out[i] = weighted / denominator;
    }
    return out;
}
/**
 * Скорость изменения, проценты за `period` баров.
 *
 * Отвечает на вопрос «насколько быстро», а не «куда»: цена может быть
 * выше средней и при этом замедляться.
 */
export function roc(values, period) {
    const out = filled(values.length);
    for (let i = period; i < values.length; i += 1) {
        const past = values[i - period];
        if (past === 0)
            continue;
        out[i] = ((values[i] - past) / Math.abs(past)) * 100;
    }
    return out;
}
/**
 * Стохастический осциллятор, линия %K.
 *
 * Где закрытие относительно диапазона за период: ноль — у минимума, сто
 * — у максимума. Нулевая ширина диапазона (рынок стоял) значения не
 * имеет, и там честнее пустота, чем деление на ноль.
 */
export function stochasticK(highs, lows, closes, period) {
    const out = filled(closes.length);
    if (period <= 0)
        return out;
    for (let i = period - 1; i < closes.length; i += 1) {
        let highest = -Infinity;
        let lowest = Infinity;
        for (let k = i - period + 1; k <= i; k += 1) {
            if (highs[k] > highest)
                highest = highs[k];
            if (lows[k] < lowest)
                lowest = lows[k];
        }
        const range = highest - lowest;
        if (range === 0)
            continue;
        out[i] = ((closes[i] - lowest) / range) * 100;
    }
    return out;
}
/**
 * Индекс товарного канала.
 *
 * Отклонение типичной цены от её среднего, нормированное средним
 * абсолютным отклонением. Множитель 0.015 исторический: он делает так,
 * что значения обычно укладываются в диапазон около ста.
 */
export function cci(highs, lows, closes, period) {
    const typical = closes.map((close, i) => (highs[i] + lows[i] + close) / 3);
    const average = sma(typical, period);
    const out = filled(closes.length);
    for (let i = period - 1; i < closes.length; i += 1) {
        const mean = average[i];
        if (mean === null || mean === undefined)
            continue;
        let deviation = 0;
        for (let k = i - period + 1; k <= i; k += 1) {
            deviation += Math.abs(typical[k] - mean);
        }
        const meanDeviation = deviation / period;
        if (meanDeviation === 0)
            continue;
        out[i] = (typical[i] - mean) / (0.015 * meanDeviation);
    }
    return out;
}
/**
 * Средний направленный индекс: сила тренда без направления.
 *
 * Высокий ADX при падении означает сильный нисходящий тренд, а не рост.
 * Путать его с направлением — частая ошибка, поэтому в каталоге он и
 * подписан «сила тренда».
 */
export function adx(highs, lows, closes, period) {
    const length = closes.length;
    const out = filled(length);
    if (period <= 0 || length < period * 2)
        return out;
    const plus = new Array(length).fill(0);
    const minus = new Array(length).fill(0);
    const range = trueRange(highs, lows, closes);
    for (let i = 1; i < length; i += 1) {
        const up = highs[i] - highs[i - 1];
        const down = lows[i - 1] - lows[i];
        plus[i] = up > down && up > 0 ? up : 0;
        minus[i] = down > up && down > 0 ? down : 0;
    }
    // Сглаживание Уайлдера: накопленная сумма, из которой каждый бар
    // вычитается её же доля. Не EMA и не SMA — так считал автор.
    const smooth = (source) => {
        const result = filled(length);
        let accumulator = 0;
        for (let i = 1; i < length; i += 1) {
            if (i <= period) {
                accumulator += source[i];
                if (i === period)
                    result[i] = accumulator;
                continue;
            }
            const previous = result[i - 1];
            if (previous === null || previous === undefined)
                continue;
            result[i] = previous - previous / period + source[i];
        }
        return result;
    };
    const smoothRange = smooth(range.map((value) => value ?? 0));
    const smoothPlus = smooth(plus);
    const smoothMinus = smooth(minus);
    const dx = filled(length);
    for (let i = 0; i < length; i += 1) {
        const trValue = smoothRange[i];
        const plusValue = smoothPlus[i];
        const minusValue = smoothMinus[i];
        if (trValue === null || trValue === undefined || trValue === 0)
            continue;
        if (plusValue === null || plusValue === undefined)
            continue;
        if (minusValue === null || minusValue === undefined)
            continue;
        const plusDi = (plusValue / trValue) * 100;
        const minusDi = (minusValue / trValue) * 100;
        const sum = plusDi + minusDi;
        if (sum === 0)
            continue;
        dx[i] = (Math.abs(plusDi - minusDi) / sum) * 100;
    }
    let sum = 0;
    let counted = 0;
    for (let i = 0; i < length; i += 1) {
        const value = dx[i];
        if (value === null || value === undefined)
            continue;
        counted += 1;
        if (counted < period) {
            sum += value;
            continue;
        }
        if (counted === period) {
            sum += value;
            out[i] = sum / period;
            continue;
        }
        const previous = out[i - 1];
        if (previous === null || previous === undefined)
            continue;
        out[i] = (previous * (period - 1) + value) / period;
    }
    return out;
}
/**
 * Балансовый объём.
 *
 * Накопленный объём со знаком направления бара. Абсолютное значение
 * смысла не имеет — важно, куда он идёт: расхождение с ценой и есть то,
 * ради чего его смотрят.
 */
export function obv(closes, volumes) {
    const out = filled(closes.length);
    if (closes.length === 0)
        return out;
    let total = 0;
    out[0] = 0;
    for (let i = 1; i < closes.length; i += 1) {
        if (closes[i] > closes[i - 1])
            total += volumes[i];
        else if (closes[i] < closes[i - 1])
            total -= volumes[i];
        out[i] = total;
    }
    return out;
}
/**
 * Скользящая средневзвешенная по объёму.
 *
 * Окно скользящее, а не с начала сессии: у криптовалют нет закрытия, и
 * «начало дня» для них — условность биржи, а не свойство рынка.
 */
export function vwap(highs, lows, closes, volumes, period) {
    const out = filled(closes.length);
    if (period <= 0)
        return out;
    // Две скользящие суммы вместо пересчёта окна на каждом баре: то же
    // число, на порядок меньше работы при длинном периоде.
    let volume = 0;
    let weighted = 0;
    for (let i = 0; i < closes.length; i += 1) {
        const typical = (highs[i] + lows[i] + closes[i]) / 3;
        weighted += typical * volumes[i];
        volume += volumes[i];
        if (i >= period) {
            const out_i = i - period;
            const oldTypical = (highs[out_i] + lows[out_i] + closes[out_i]) / 3;
            weighted -= oldTypical * volumes[out_i];
            volume -= volumes[out_i];
        }
        if (i < period - 1)
            continue;
        if (volume === 0)
            continue;
        out[i] = weighted / volume;
    }
    return out;
}
/**
 * Относительный объём: во сколько раз больше обычного.
 *
 * Единица — обычный объём для этого инструмента, два — вдвое больше
 * среднего. Сравнивать абсолютные объёмы разных инструментов
 * бессмысленно, а это число сравнимо.
 */
export function relativeVolume(volumes, period) {
    const average = sma(volumes, period);
    return volumes.map((value, i) => {
        const mean = average[i];
        if (mean === null || mean === undefined || mean === 0)
            return null;
        return value / mean;
    });
}
/**
 * Супертренд: линия поддержки или сопротивления по ATR.
 *
 * Состояние переносится с бара на бар — линия не пересчитывается заново,
 * а подтягивается и переворачивается при пробое. Из-за этой памяти
 * супертренд нельзя посчитать по одному бару в отрыве от предыдущих.
 */
export function supertrend(highs, lows, closes, period, multiplier) {
    const length = closes.length;
    const out = filled(length);
    const atrValues = atr(highs, lows, closes, period);
    let upper = 0;
    let lower = 0;
    let trendUp = true;
    let started = false;
    for (let i = 0; i < length; i += 1) {
        const range = atrValues[i];
        if (range === null || range === undefined)
            continue;
        const middle = (highs[i] + lows[i]) / 2;
        const rawUpper = middle + multiplier * range;
        const rawLower = middle - multiplier * range;
        if (!started) {
            upper = rawUpper;
            lower = rawLower;
            trendUp = closes[i] >= middle;
            started = true;
            out[i] = trendUp ? lower : upper;
            continue;
        }
        // Границы только сужаются, пока цена их не пробила: иначе линия
        // дёргалась бы вслед за каждым баром и перестала быть уровнем.
        upper = closes[i - 1] > upper ? rawUpper : Math.min(rawUpper, upper);
        lower = closes[i - 1] < lower ? rawLower : Math.max(rawLower, lower);
        if (trendUp && closes[i] < lower)
            trendUp = false;
        else if (!trendUp && closes[i] > upper)
            trendUp = true;
        out[i] = trendUp ? lower : upper;
    }
    return out;
}
/** Линия MACD и её сигнальная: разность двух EMA и EMA от неё. */
export function macd(closes, fast, slow, signalPeriod) {
    const fastEma = ema(closes, fast);
    const slowEma = ema(closes, slow);
    const line = closes.map((_, i) => {
        const a = fastEma[i];
        const b = slowEma[i];
        return a === null || a === undefined || b === null || b === undefined ? null : a - b;
    });
    /*
     * Сигнальная считается по значениям линии, а прогревочный участок у
     * неё пустой. Подставить туда нули значило бы получить ложное
     * пересечение на первых барах — ровно там, где данных ещё нет.
     */
    const dense = [];
    const index = [];
    line.forEach((value, i) => {
        if (value === null)
            return;
        dense.push(value);
        index.push(i);
    });
    const signalDense = ema(dense, signalPeriod);
    const signal = filled(closes.length);
    index.forEach((position, k) => {
        signal[position] = signalDense[k] ?? null;
    });
    const histogram = line.map((value, i) => {
        const s = signal[i];
        return value === null || s === null || s === undefined ? null : value - s;
    });
    return { line, signal, histogram };
}
//# sourceMappingURL=ta.js.map